// Curated color source for produce commodities — the app-wide commodity colorway.
//
// This is the extraction of the COMMODITY_COLORS map that Package Index
// (`ItemCard.jsx`, `PluDetailPanel.jsx`, `BagDetailPanel.jsx`) and the Customer
// Specs index card have each carried their own private copy of; those copies are
// byte-identical in values, so a shared module is the single source of truth for
// any NEW surface that renders a commodity pill. Same `{ bg, border }` return
// shape as `gradeColor.js`, so it drives the existing Pill / SizeTag styled
// components directly. Backgrounds are dark/saturated so white text stays legible.
//
// Raw hex is the documented colorway exception for the commodity and grade
// palettes (see [[the-vault-colorway]]) — these are data-keyed identity colors,
// not theme surface colors, so they are not tokens.
//
// A newly created commodity needs no code change: it falls through to the
// deterministic HSL hash below and gets a stable, white-text-legible color.

const COMMODITY_COLORS = {
  'Organic Cara Cara': { bg: '#C85C1E', border: '#9E4818' }, // rich orange
  'Organic Navel':     { bg: '#BF601A', border: '#954C15' },
  'Organic Grapefruit':{ bg: '#B4433F', border: '#8E3531' }, // ruby grapefruit
  'Pummelo':           { bg: '#6A9E3A', border: '#4F772B' }, // greenish rind
  'Valencia':          { bg: '#B15818', border: '#8C4513' },
  'Organic Mandarin':  { bg: '#C25E1C', border: '#9A4C17' },
  'Mandarin':          { bg: '#C25E1C', border: '#9A4C17' },
  'Minneola':          { bg: '#D0681E', border: '#A65218' }, // tangelo deep orange
  'Navel':             { bg: '#B65A19', border: '#8F4714' },
  'Gold Nuggets':      { bg: '#A66D00', border: '#7D5200' }, // golden
  'Lemon':             { bg: '#9E8C0A', border: '#7A6D08' }, // darker lemon → white text OK
  'Cara Cara':         { bg: '#C85C1E', border: '#9E4818' },
  'Blood':             { bg: '#7B2424', border: '#5E1B1B' }, // deep blood orange
  'Grapefruit':        { bg: '#B4433F', border: '#8E3531' },
  'Organic Blood':     { bg: '#7B2424', border: '#5E1B1B' },
  'Organic Lemon':     { bg: '#9E8C0A', border: '#7A6D08' },
  'Organic Minneola':  { bg: '#D0681E', border: '#A65218' },
  'Organic Valencia':  { bg: '#B15818', border: '#8C4513' },
  'Lime':              { bg: '#2E7D32', border: '#205A23' }, // lime green
  'Midknight':         { bg: '#9C4F17', border: '#783D12' }, // valencia cultivar
};

// Deterministic, white-text-legible fallback for any uncurated commodity name.
// Identical formula to the four in-file copies, so a name keeps its hue.
const hashColor = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return { bg: `hsl(${hue} 65% 35%)`, border: `hsl(${hue} 70% 25%)` };
};

// The "Any commodity" / unassigned state — neutral grey, matching the other
// sentinel pills on the Bag Traceability page.
export const COMMODITY_NEUTRAL = { bg: 'var(--grey-600)', border: 'var(--grey-700)' };

// Lookup is case-SENSITIVE on the curated map (matching the four existing copies,
// which key on the exact `Commodities.Commodity` spelling); anything else hashes.
export function commodityColor(name) {
  const key = String(name == null ? '' : name).trim();
  return COMMODITY_COLORS[key] || hashColor(key);
}

export default commodityColor;
